/**
 * Life4Earth V2 — Problem Workspaces API Adapter
 *
 * CRUD for problem workspaces, team members, and phase transitions.
 * Endpoint: /api/ProblemWorkspaces
 */

import { apiFetch } from "./client";

// ── Response Types ──────────────────────────────────────────

export type WorkspaceScopeType = "personal" | "household" | "community";

export interface WorkspaceResponse {
  workspaceGuid: string;
  communityGuid: string | null;
  title: string | null;
  presentingConcern: string;
  concernCategory: string | null;
  globalProblemGuid: string | null;
  scopeType: WorkspaceScopeType;
  requiredPhases: string[] | null;
  affectedScopeDescription: string | null;
  phase: string;
  phaseEnteredAt: string;
  subPhase: string | null;
  subPhaseEnteredAt: string | null;
  stageGateData: string;
  teamLeadGuid: string;
  isActive: boolean;
  isBeta: boolean;
  heroImageUrl: string | null;
  detailHeroImageUrl: string | null;
  description: string | null;
  summaryFindings: string | null;
  initialAnalysis: string | null;
  presentingConcernValidatedAt: string | null;
  presentingConcernValidatedBy: string | null;
  autoCollectionGuid: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  teamMemberCount: number;
  evidenceCount: number;
  solutionCount: number;
  subphases?: SubphaseInfo[];
}

export interface SubphaseInfo {
  subPhase: string;
  displayName: string;
  description: string | null;
  behavior: "step" | "checkpoint" | "shortcut_exit";
  sortOrder: number;
  isRequired: boolean;
}

export interface PhaseSubphaseResponse {
  phase: string;
  subPhase: string;
  sortOrder: number;
  displayName: string;
  description: string | null;
  behavior: "step" | "checkpoint" | "shortcut_exit";
  isRequiredByScope: string | null;
  checkpointCriteria: string | null;
}

export interface TeamMemberResponse {
  id: string;
  workspaceGuid: string;
  userGuid: string;
  role: string;
  joinedAt: string;
}

// ── Request Types ───────────────────────────────────────────

export interface CreateWorkspaceRequest {
  communityGuid?: string;
  presentingConcern: string;
  teamLeadGuid: string;
  scopeType?: WorkspaceScopeType;
  concernCategory?: string;
  heroImageUrl?: string;
  detailHeroImageUrl?: string;
  description?: string;
  summaryFindings?: string;
  fromClusterGuid?: string;
}

export interface UpdateWorkspaceRequest {
  presentingConcern?: string;
  title?: string;
  scopeType?: WorkspaceScopeType;
  communityGuid?: string;
  concernCategory?: string;
  globalProblemGuid?: string;
  heroImageUrl?: string;
  detailHeroImageUrl?: string;
  description?: string;
  summaryFindings?: string;
  initialAnalysis?: string;
  isBeta?: boolean;
}

export interface TransitionPhaseRequest {
  toPhase: string;
  rationale?: string;
}

export interface TransitionSubphaseRequest {
  toSubPhase: string;
  notes?: string;
}

export interface AddTeamMemberRequest {
  userGuid: string;
  role?: string;
}

// ── Workspace Phases (ordered) ──────────────────────────────

export const WORKSPACE_PHASES = [
  "signal_detection",
  "detection_validation",
  "analysis",
  "solution_design",
  "resourcing",
  "implementation",
  "sustainment",
] as const;

export type WorkspacePhase = typeof WORKSPACE_PHASES[number];

// ── API Functions ───────────────────────────────────────────

export async function fetchWorkspaces(params?: {
  communityGuid?: string;
  userGuid?: string;
  phase?: string;
  isActive?: boolean;
}): Promise<WorkspaceResponse[]> {
  const qs = new URLSearchParams();
  if (params?.communityGuid) qs.set("communityGuid", params.communityGuid);
  if (params?.userGuid) qs.set("userGuid", params.userGuid);
  if (params?.phase) qs.set("phase", params.phase);
  if (params?.isActive !== undefined) qs.set("isActive", String(params.isActive));
  const query = qs.toString();
  return apiFetch<WorkspaceResponse[]>(
    `/api/ProblemWorkspaces${query ? `?${query}` : ""}`
  );
}

export async function fetchWorkspaceByGuid(
  workspaceGuid: string
): Promise<WorkspaceResponse> {
  return apiFetch<WorkspaceResponse>(
    `/api/ProblemWorkspaces/${workspaceGuid}`
  );
}

export async function createWorkspace(
  request: CreateWorkspaceRequest
): Promise<WorkspaceResponse> {
  return apiFetch<WorkspaceResponse>("/api/ProblemWorkspaces", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function updateWorkspace(
  workspaceGuid: string,
  request: UpdateWorkspaceRequest
): Promise<WorkspaceResponse> {
  return apiFetch<WorkspaceResponse>(
    `/api/ProblemWorkspaces/${workspaceGuid}`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

export async function transitionWorkspacePhase(
  workspaceGuid: string,
  request: TransitionPhaseRequest
): Promise<void> {
  return apiFetch<void>(
    `/api/ProblemWorkspaces/${workspaceGuid}/transition`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

/** Soft-delete a workspace (team lead / creator / sysadmin). Hides it
 *  everywhere; restorable until a sysadmin hard-deletes it (migration 250). */
export async function deleteWorkspace(
  workspaceGuid: string
): Promise<void> {
  return apiFetch<void>(
    `/api/ProblemWorkspaces/${workspaceGuid}`,
    { method: "DELETE" }
  );
}

/** Restore a soft-deleted workspace (owner or sysadmin). */
export async function restoreWorkspace(workspaceGuid: string): Promise<void> {
  return apiFetch<void>(`/api/ProblemWorkspaces/${workspaceGuid}/restore`, { method: "POST" });
}

/** Admin-console summary row (all workspaces, including soft-deleted). */
export interface AdminWorkspaceSummary {
  workspaceGuid: string;
  title: string | null;
  presentingConcern: string;
  phase: string;
  scopeType: string;
  communityDisplayName: string | null;
  teamLeadDisplayName: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** Sysadmin: list every workspace (incl. soft-deleted) for the cleanup console. */
export async function fetchAdminWorkspaces(params?: { search?: string }): Promise<AdminWorkspaceSummary[]> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set("search", params.search);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch<AdminWorkspaceSummary[]>(`/api/ProblemWorkspaces/admin/all${suffix}`);
}

/** Sysadmin: permanently hard-delete a workspace and all dependent rows. */
export async function hardDeleteWorkspace(
  workspaceGuid: string
): Promise<{ message: string; recordsRemoved: Record<string, number> }> {
  return apiFetch<{ message: string; recordsRemoved: Record<string, number> }>(
    `/api/ProblemWorkspaces/${workspaceGuid}/hard`,
    { method: "DELETE" }
  );
}

// ── Team Members ────────────────────────────────────────────

export async function fetchTeamMembers(
  workspaceGuid: string
): Promise<TeamMemberResponse[]> {
  return apiFetch<TeamMemberResponse[]>(
    `/api/ProblemWorkspaces/${workspaceGuid}/team`
  );
}

export async function addTeamMember(
  workspaceGuid: string,
  request: AddTeamMemberRequest
): Promise<TeamMemberResponse> {
  return apiFetch<TeamMemberResponse>(
    `/api/ProblemWorkspaces/${workspaceGuid}/team`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

export async function removeTeamMember(
  workspaceGuid: string,
  userGuid: string
): Promise<void> {
  return apiFetch<void>(
    `/api/ProblemWorkspaces/${workspaceGuid}/team/${userGuid}`,
    { method: "DELETE" }
  );
}

// ── Subphases ──────────────────────────────────────────────

/** Fetch subphase reference data, optionally filtered by phase. */
export async function fetchSubphases(
  phase?: string
): Promise<PhaseSubphaseResponse[]> {
  const qs = phase ? `?phase=${encodeURIComponent(phase)}` : "";
  return apiFetch<PhaseSubphaseResponse[]>(
    `/api/ProblemWorkspaces/subphases${qs}`
  );
}

/** Transition workspace to a new subphase within the current phase. */
export async function transitionWorkspaceSubphase(
  workspaceGuid: string,
  request: TransitionSubphaseRequest
): Promise<WorkspaceResponse> {
  return apiFetch<WorkspaceResponse>(
    `/api/ProblemWorkspaces/${workspaceGuid}/subphase-transition`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

/** Initialize subphase tracking for a workspace (sets to first subphase). */
export async function initializeWorkspaceSubphase(
  workspaceGuid: string
): Promise<WorkspaceResponse> {
  return apiFetch<WorkspaceResponse>(
    `/api/ProblemWorkspaces/${workspaceGuid}/initialize-subphase`,
    { method: "POST" }
  );
}
